import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";
import {
  FaArrowLeft,
  FaCamera,
  FaEnvelope,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaPhone,
} from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import { formatDate, formatDateTime } from "../../utils/date.js";
import { getInspectionJobSheet } from "../services/inspectionApi.js";
import { cacheJobSheet, getCachedJobSheet } from "../storage/inspectionStore.js";

// 1.0's printed job sheet, on the phone. The office hands inspectors a printout to
// take to the job; everything on it is already in the database, so this carries it
// instead. Deliberately a screen of its own behind a Job Sheet button (Robert
// 2026-07-30): the photo screen stays about photos, and this is what you read.
//
// Better than the paper in three ways, because it can be: the address opens maps,
// the phone numbers dial, the email composes.

// Label ABOVE the value on a phone, beside it from tablets up. A fixed label
// column would leave a 360px screen about 200px for an address, and Robert's first
// requirement for this screen was that it not feel cramped.
function Line({ label, children }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <div className="d-flex flex-column flex-sm-row gap-sm-2 py-2 py-sm-1 border-bottom border-light-subtle">
      <div className="small text-body-secondary flex-shrink-0" style={{ width: "9rem" }}>{label}</div>
      <div className="fw-medium text-break">{children}</div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <Card className="mb-3 shadow-sm border-0">
      <Card.Body>
        <h2 className="h6 text-uppercase text-body-secondary mb-2">{title}</h2>
        {children}
      </Card.Body>
    </Card>
  );
}

function InspectionJobSheetPage({ currentUser }) {
  const { inspectionId } = useParams();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cached, setCached] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getInspectionJobSheet(inspectionId);
      setSheet(result);
      setCached(false);
      await cacheJobSheet(currentUser.id, inspectionId, result);
    } catch (requestError) {
      const saved = await getCachedJobSheet(currentUser.id, inspectionId).catch(() => null);
      if (saved) {
        setSheet(saved);
        setCached(true);
      } else {
        setError(requestError.response?.data?.error || "Unable to load this job sheet.");
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser.id, inspectionId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="text-center py-5 text-body-secondary"><Spinner size="sm" /> Loading job sheet…</div>;
  }
  if (!sheet) return <Alert variant="danger">{error || "Job sheet not available."}</Alert>;

  const back = `/inspection/${inspectionId}`;
  const phones = [
    ["Mobile", sheet.mobile_phone],
    ["Business", sheet.business_phone],
  ].filter(([, value]) => value);
  const mapUrl = sheet.street_address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sheet.street_address)}`
    : null;

  return (
    <div className="inspection-page mx-auto">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Link to={back} className="btn btn-outline-secondary"><FaArrowLeft /></Link>
        <div className="flex-grow-1">
          <h1 className="h4 mb-0">Job Sheet {sheet.claim_number}</h1>
          <div className="small text-body-secondary">
            {sheet.job_type_name || "Job"} · Inspection #{sheet.seq}
          </div>
        </div>
        {sheet.status_name ? <Badge bg="secondary">{sheet.status_name}</Badge> : null}
      </div>

      {cached ? <Alert variant="warning" className="py-2">Saved copy — you are offline.</Alert> : null}

      {/* The scope of works: the reason the printout exists, so it goes first and
          keeps the line breaks the office typed. */}
      <Block title="Work / Quote Request Details">
        {sheet.work_request_details ? (
          <div className="text-break" style={{ whiteSpace: "pre-wrap" }}>{sheet.work_request_details}</div>
        ) : (
          <div className="text-body-secondary">
            Nothing recorded on the job. Ask the office before you leave.
          </div>
        )}
        {sheet.other_info ? (
          <>
            <div className="text-uppercase small text-body-secondary mt-3">Other Info</div>
            <div className="text-break" style={{ whiteSpace: "pre-wrap" }}>{sheet.other_info}</div>
          </>
        ) : null}
      </Block>

      <Block title="Site">
        <Line label="Client">{sheet.client_name}</Line>
        <Line label="Contact">
          {sheet.contact_name && sheet.contact_name !== sheet.client_name ? sheet.contact_name : null}
        </Line>
        <Line label="Address">
          {mapUrl ? (
            <a href={mapUrl} target="_blank" rel="noreferrer">
              <FaMapMarkerAlt className="me-1" />{sheet.street_address}
            </a>
          ) : null}
        </Line>
        {phones.map(([label, value]) => (
          <Line key={label} label={`${label} phone`}>
            <a href={`tel:${value}`}><FaPhone className="me-1" />{value}</a>
          </Line>
        ))}
        <Line label="Email">
          {sheet.email_address ? (
            <a href={`mailto:${sheet.email_address}`} className="text-break">
              <FaEnvelope className="me-1" />{sheet.email_address}
            </a>
          ) : null}
        </Line>
        <Line label="Age of property">{sheet.property_age}</Line>
        <Line label="Lockbox">{sheet.lockbox_location}</Line>
      </Block>

      <Block title="Insurance">
        <Line label="Insurer / debtor">{sheet.insurer_name}</Line>
        <Line label="Assessor">{sheet.assessor_name}</Line>
        <Line label="Assessor phone">
          {sheet.assessor_phone ? (
            <a href={`tel:${sheet.assessor_phone}`}><FaPhone className="me-1" />{sheet.assessor_phone}</a>
          ) : null}
        </Line>
        <Line label="Assessor ref">{sheet.assessor_ref}</Line>
        <Line label="Insurance claim">{sheet.insurance_claim_number}</Line>
      </Block>

      <Block title="Office">
        <Line label="Log date">{sheet.logged_at ? formatDateTime(sheet.logged_at) : null}</Line>
        <Line label="Who">{sheet.coordinator_name}</Line>
        <Line label="Inspector">{sheet.inspector_name}</Line>
        <Line label="Inspection date">
          {sheet.scheduled_date
            ? `${formatDate(sheet.scheduled_date, "long")}${sheet.scheduled_confirmed ? " (confirmed)" : ""}`
            : null}
        </Line>
        <Line label="Notes for you">
          {sheet.notes_for_inspector ? (
            <span style={{ whiteSpace: "pre-wrap" }}>{sheet.notes_for_inspector}</span>
          ) : null}
        </Line>
        <Line label="Job notes">
          {sheet.notes ? <span style={{ whiteSpace: "pre-wrap" }}>{sheet.notes}</span> : null}
        </Line>
      </Block>

      {/* The red block at the foot of the printed sheet, kept word for word — it is
          the only health-and-safety prompt an inspector sees on site. */}
      <Alert variant="danger" className="text-center fw-bold">
        <div className="text-uppercase"><FaExclamationTriangle className="me-2" />Inspector info</div>
        <div className="mt-2">HEALTH &amp; SAFETY</div>
        <div>Is the work area safe to enter?</div>
        <div className="mt-2">REMINDER: TAKE A PHOTO OF THE HOUSE FROM THE STREET</div>
      </Alert>

      <div className="d-grid gap-2 mb-4">
        <Link to={back} className="btn btn-primary btn-lg">
          <FaCamera className="me-2" />Back to Photos
        </Link>
      </div>
    </div>
  );
}

export default InspectionJobSheetPage;
